import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, PackageCheck, Send, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { LogoMark } from "@/components/Logo";
import LineItemsSection, { newLineItem } from "@/components/LineItemsSection";
import PrintExportButtons from "@/components/PrintExportButtons";

const REF_TABS = [
  { key: "po_number", label: "PO Number" },
  { key: "invoice_number", label: "Invoice #" },
  { key: "job_number", label: "Job #" },
  { key: "customer_reference", label: "Customer Ref" },
  { key: "internal_reference", label: "Internal Ref" },
];

const emptyParty = { company: "", contact_name: "", email: "", phone: "", address: "" };

function PartyForm({ prefix, party, setParty, suppliers, testidPrefix, saveForFuture, setSaveForFuture, company }) {
  const apply = (s) => setParty({
    company: s.name, contact_name: s.contact_name || "", email: s.email, phone: s.phone || "", address: s.address || "",
  });
  const applyMyCompany = () => setParty({
    company: company?.company_name || "",
    contact_name: company?.company_contact_name || "",
    email: company?.company_email || "",
    phone: company?.company_phone || "",
    address: company?.company_address || "",
  });
  const hasCompany = !!(company?.company_name || company?.company_address);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {hasCompany && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyMyCompany}
            data-testid={`${testidPrefix}-use-my-company`}
            className="rounded-none border-2 h-9 text-[11px] uppercase tracking-[0.15em] font-bold hover:border-orange-600 hover:text-orange-600"
          >
            Use my company
          </Button>
        )}
        {suppliers?.length > 0 && (
          <div className="flex-1 min-w-[200px]">
            <Select onValueChange={(v) => { const s = suppliers.find((x) => x.supplier_id === v); if (s) apply(s); }}>
              <SelectTrigger data-testid={`${testidPrefix}-supplier-select`} className="rounded-none h-9 border-2 text-xs">
                <SelectValue placeholder="Load from saved contacts..." />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Company" required v={party.company} on={(v) => setParty({ ...party, company: v })} testid={`${testidPrefix}-company`} />
        <Field label="Contact Name" v={party.contact_name} on={(v) => setParty({ ...party, contact_name: v })} testid={`${testidPrefix}-contact`} />
        <Field label="Email · comma-separate for multiple" v={party.email} on={(v) => setParty({ ...party, email: v })} testid={`${testidPrefix}-email`} placeholder="ops@supplier.com, warehouse@supplier.com" mono />
        <Field label="Phone" v={party.phone} on={(v) => setParty({ ...party, phone: v })} testid={`${testidPrefix}-phone`} />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Address *</Label>
        <Textarea
          data-testid={`${testidPrefix}-address`}
          value={party.address}
          onChange={(e) => setParty({ ...party, address: e.target.value })}
          required
          rows={2}
          className="rounded-none border-2 focus-visible:ring-orange-500"
        />
      </div>
      <label className="flex items-start gap-3 border-2 border-dashed border-border p-3 cursor-pointer hover:border-orange-600 transition-colors duration-200">
        <Checkbox
          data-testid={`${testidPrefix}-save-future`}
          checked={saveForFuture}
          onCheckedChange={setSaveForFuture}
          className="mt-0.5 rounded-none border-2 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
        />
        <div className="text-xs">
          <div className="font-bold uppercase tracking-[0.15em]">Save these details for future</div>
          <div className="text-muted-foreground mt-0.5">Store this contact so you can pick it from the saved list next time.</div>
        </div>
      </label>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testid, placeholder, mono }) {
  return (
    <div className="space-y-2">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold whitespace-nowrap">
        {label}{required && " *"}
      </Label>
      <Input
        type={type}
        data-testid={testid}
        value={v}
        onChange={(e) => on(e.target.value)}
        required={required}
        placeholder={placeholder}
        className={`h-10 rounded-none border-2 focus-visible:ring-orange-500 ${mono ? "font-mono text-xs" : ""}`}
      />
    </div>
  );
}

export default function CreateRequest() {
  const nav = useNavigate();
  const [requestType, setRequestType] = useState("pickup");
  const [priority, setPriority] = useState("standard");
  const [pickup, setPickup] = useState({ ...emptyParty });
  const [delivery, setDelivery] = useState({ ...emptyParty });
  const [savePickup, setSavePickup] = useState(false);
  const [saveDelivery, setSaveDelivery] = useState(false);
  const [references, setReferences] = useState({ po_number: "", invoice_number: "", job_number: "", customer_reference: "", internal_reference: "" });
  const [items, setItems] = useState([newLineItem()]);
  const [instructions, setInstructions] = useState("");
  const [courier, setCourier] = useState({ courier_id: "", company: "", email: "" });
  const [courierAddNew, setCourierAddNew] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [s, c, co] = await Promise.all([api.get("/suppliers"), api.get("/couriers"), api.get("/company").catch(() => ({ data: null }))]);
      setSuppliers(s.data);
      setCouriers(c.data);
      setCompany(co.data);
    })();
    // Load once on mount. api is a stable module import; state setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = () => setItems([...items, newLineItem()]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        request_type: requestType,
        priority,
        pickup,
        delivery,
        courier: {
          courier_id: courier.courier_id || null,
          company: courier.company,
          email: courier.email,
        },
        references,
        items: items
          .filter((i) => i.description)
          .map((i) => ({
            item_number: i.item_number || "",
            description: i.description,
            quantity: Number(i.quantity) || 0,
            package_type: i.package_type || "",
            dimensions: i.dimensions || "",
            weight: i.weight === "" || i.weight == null ? null : Number(i.weight),
            package_group: i.package_group && i.package_group !== "0" ? parseInt(i.package_group, 10) : null,
          })),
        instructions,
      };
      const { data } = await api.post("/requests", payload);

      // Save contacts if requested (fire-and-forget)
      const saves = [];
      if (savePickup && pickup.email && pickup.company) {
        saves.push(api.post("/suppliers", {
          name: pickup.company, contact_name: pickup.contact_name || "",
          email: pickup.email, phone: pickup.phone || "", address: pickup.address || "",
        }).catch(() => null));
      }
      if (saveDelivery && delivery.email && delivery.company) {
        saves.push(api.post("/suppliers", {
          name: delivery.company, contact_name: delivery.contact_name || "",
          email: delivery.email, phone: delivery.phone || "", address: delivery.address || "",
        }).catch(() => null));
      }
      if (saves.length) await Promise.all(saves);

      toast.success(`Request ${data.reference_number} dispatched`, {
        description: "Supplier, courier and you (requestor) have been notified" +
          (saves.length ? ` · ${saves.length} contact${saves.length > 1 ? "s" : ""} saved` : ""),
      });
      nav(`/requests/${data.request_id}`);
    } catch (err) {
      toast.error("Failed to submit", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2">
          <LogoMark size={44} className="text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In &middot; New Movement
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Log request</h1>
        </div>
        <PrintExportButtons
          targetRef={printRef}
          fileName={`new-request-${new Date().toISOString().slice(0, 10)}`}
          testidPrefix="new-request-export"
        />
      </div>

      <div ref={printRef} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <section className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Movement Type
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              {[
                { key: "pickup", label: "PICK UP", Icon: ArrowUpFromLine },
                { key: "delivery", label: "DELIVERY", Icon: ArrowDownToLine },
              ].map(({ key, label, Icon }) => {
                const active = requestType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`type-${key}-button`}
                    onClick={() => setRequestType(key)}
                    aria-pressed={active}
                    className={`border-2 p-5 text-left transition-colors duration-200 flex items-center gap-4 ${
                      active
                        ? "border-orange-600 bg-orange-600 text-white"
                        : "border-border bg-transparent text-foreground hover:border-foreground"
                    }`}
                  >
                    <Icon className="h-8 w-8 flex-shrink-0" strokeWidth={2.5} />
                    <div>
                      <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${active ? "text-white/70" : "text-muted-foreground"}`}>
                        {active ? "Selected" : "Choose"}
                      </div>
                      <div className="font-heading text-2xl font-black tracking-tight leading-none mt-1">{label}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {(requestType === "pickup"
            ? [
                { title: "Pick Up From", Icon: ArrowUpFromLine, party: pickup, setParty: setPickup, prefix: "pickup", save: savePickup, setSave: setSavePickup },
                { title: "Deliver To", Icon: ArrowDownToLine, party: delivery, setParty: setDelivery, prefix: "delivery", save: saveDelivery, setSave: setSaveDelivery },
              ]
            : [
                { title: "Deliver To", Icon: ArrowDownToLine, party: delivery, setParty: setDelivery, prefix: "delivery", save: saveDelivery, setSave: setSaveDelivery },
                { title: "From", Icon: ArrowUpFromLine, party: pickup, setParty: setPickup, prefix: "pickup", save: savePickup, setSave: setSavePickup },
              ]
          ).map(({ title, Icon, party, setParty, prefix, save, setSave }) => (
            <section key={prefix} className="border-2 border-border bg-card">
              <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-orange-600" />
                {title}
              </div>
              <div className="p-6">
                <PartyForm
                  party={party}
                  setParty={setParty}
                  suppliers={suppliers}
                  testidPrefix={prefix}
                  saveForFuture={save}
                  setSaveForFuture={setSave}
                  company={company}
                />
              </div>
            </section>
          ))}

          <section className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Reference Numbers
            </div>
            <div className="p-6">
              <Tabs defaultValue="po_number">
                <TabsList className="rounded-none bg-transparent border-b-2 border-border w-full justify-start p-0 h-auto">
                  {REF_TABS.map((t) => (
                    <TabsTrigger
                      key={t.key}
                      value={t.key}
                      data-testid={`ref-tab-${t.key}`}
                      className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold"
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {REF_TABS.map((t) => (
                  <TabsContent key={t.key} value={t.key} className="mt-4">
                    <Input
                      data-testid={`ref-input-${t.key}`}
                      value={references[t.key]}
                      onChange={(e) => setReferences({ ...references, [t.key]: e.target.value })}
                      placeholder={`Enter ${t.label}`}
                      className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </section>

          <LineItemsSection items={items} setItems={setItems} prefix="li" />

          <section className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Special Instructions
            </div>
            <div className="p-6">
              <Textarea data-testid="instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} className="rounded-none border-2" placeholder="e.g., Fragile handling, delivery hours..." />
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-6 lg:self-start">
          <section className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Courier
            </div>
            <div className="p-6 space-y-4">
              {!courierAddNew ? (
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Assign courier *</Label>
                  <Select value={courier.courier_id} onValueChange={(v) => {
                    if (v === "__new__") { setCourierAddNew(true); setCourier({ courier_id: "", company: "", email: "" }); return; }
                    const c = couriers.find((x) => x.courier_id === v);
                    if (c) setCourier({ courier_id: c.courier_id, company: c.name, email: c.email });
                  }}>
                    <SelectTrigger data-testid="courier-select" className="rounded-none h-10 border-2">
                      <SelectValue placeholder="Select courier..." />
                    </SelectTrigger>
                    <SelectContent>
                      {couriers.map((c) => (
                        <SelectItem key={c.courier_id} value={c.courier_id}>{c.name}</SelectItem>
                      ))}
                      <SelectItem value="__new__" data-testid="courier-add-new-option">+ Add new courier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <Field label="Courier company" required v={courier.company} on={(v) => setCourier({ ...courier, company: v })} testid="courier-new-company" />
                  <Field label="Courier email · comma-separate for multiple" required v={courier.email} on={(v) => setCourier({ ...courier, email: v })} testid="courier-new-email" placeholder="ops@courier.com, dispatch@courier.com" mono />
                  <Button type="button" size="sm" variant="outline" onClick={() => { setCourierAddNew(false); setCourier({ courier_id: "", company: "", email: "" }); }} className="rounded-none border-2 h-8 text-xs">
                    Use saved instead
                  </Button>
                </>
              )}
              {courier.company && (
                <div className="text-xs text-muted-foreground border-t border-border pt-3">
                  <div><span className="font-bold">Notifying:</span> {courier.company}</div>
                  <div className="font-mono">{courier.email}</div>
                </div>
              )}
            </div>
          </section>

          <section className="border-2 border-border bg-card">
            <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Priority
            </div>
            <div className="p-6">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="priority-select" className="rounded-none h-10 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="same_day">Same Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          <div className="border-2 border-orange-600 bg-orange-50 dark:bg-orange-950 p-4 text-xs text-orange-800 dark:text-orange-200 leading-relaxed flex gap-3">
            <PackageCheck className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              A unique reference number (format <span className="font-mono font-bold">DR-YYYYMM-####</span>) is
              assigned automatically. On submit, the supplier, the courier, and you (the requestor)
              each receive a copy of the movement.
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="submit-request-button"
            className="w-full h-12 rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-[0.2em] text-xs font-bold"
          >
            <Send className="h-4 w-4 mr-2" />
            {saving ? "Dispatching..." : "Submit & Notify"}
          </Button>
        </aside>
      </div>
    </form>
  );
}
